<template>
   <div class="card">
      <div class="card-body m-2">
         <div class="row p-2 align-items-center">

            <!-- Search -->
            <div class="col-12 col-lg-4 mb-2 mb-lg-0 d-flex gap-2">
               <input v-model="search" type="text" class="form-control form-control-sm"
                  placeholder="&#xF002; Search Extension Key..."
                  style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free'; font-weight: 900;"
                  @keyup="onSearch()" />
            </div>

            <!-- Filters + Pagination -->
            <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Sort by:</span>
                  <select v-model="sortBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="s in sortByList" :value="s.value" :key="s.value">{{ s.text }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Rows per page:</span>
                  <select v-model="pagination.pageSize"
                     class="form-select form-select-sm shadow-none filterOptions bordered-select" style="width: auto;">
                     <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}</option>
                  </select>
               </div>

               <div class="pagination" v-if="pagination.totalItems > 0">
                  <button @click="previousPage()" :disabled="pagination.page == 1" class="pagination-arrow">
                     <i class="fa fa-chevron-left"></i>
                  </button>
                  <span class="pagination-label">
                     <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                  </span>
                  <button @click="nextPage()"
                     :disabled="pagination.page == pagination.totalPages || pagination.totalItems == 0"
                     class="pagination-arrow">
                     <i class="fa fa-chevron-right"></i>
                  </button>
               </div>

            </div>
         </div>

         <!-- Table -->
         <div class="row p-2">
            <table class="table align-middle mainTable">
               <thead class="align-middle">
                  <tr>
                     <th scope="col" v-for="header in headers" :key="header.value">{{ header.text }}</th>
                  </tr>
               </thead>

               <tbody v-if="pagination.totalItems <= 0">
                  <tr>
                     <td colspan="3">No records to display.</td>
                  </tr>
               </tbody>

               <tbody v-else>
                  <tr v-for="(apiKey, index) in unallocatedExtensionKeys" :key="index">
                     <td style="font-family: monospace; font-size: 12px;">
                        {{ apiKey.api_key }}
                     </td>
                     <td>{{ apiKey.ip || '—' }}</td>
                     <td>
                        <button type="button" class="btn btn-outline-info btn-sm"
                           style="border-radius: 8px !important;"
                           title="Allocate Extension Key"
                           @click="openAllocateModal(apiKey, index)">
                           <i class="fa-solid fa-share me-1"></i> Allocate
                        </button>
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>
      </div>
   </div>

   <!-- Allocate Modal -->
   <div class="modal-backdrop-custom" v-if="showAllocateModal" @click.self="closeAllocateModal()">
      <div class="modal-content-custom">
         <button @click="closeAllocateModal()" class="close-button-custom">&times;</button>
         <h5 class="modal-title-custom">
            <i class="fa-solid fa-share me-2" style="color: #00a0c1;"></i>
            Allocate Extension Key
         </h5>
         <hr>
         <div class="modal-body-custom">
            <div class="mb-3">
               <label class="filter-label">Extension Key</label>
               <div class="key-display">{{ selectedApiKey ? selectedApiKey.api_key : '' }}</div>
            </div>

            <div class="mb-1">
               <label class="filter-label">Organisation</label>
               <ModelSelect
                  v-model="selectedOrgUuid"
                  :options="options"
                  :filterable="true"
                  placeholder="Search XSurfLog Organisations...">
               </ModelSelect>
            </div>
         </div>
         <div class="modal-footer-custom">
            <button type="button" class="btn btn-sm btn-modal-cancel" @click="closeAllocateModal()">Cancel</button>
            <button type="button" class="btn btn-sm text-light" style="background: #00a0c1; border-radius: 8px;"
               :disabled="!selectedOrgUuid"
               @click="allocateExtensionKeyToOrganisation()">
               <i class="fa-solid fa-share me-1"></i> Allocate
            </button>
         </div>
      </div>
   </div>
</template>

<script>
import moment from 'moment'
import { debounce, extractErrorMessage } from '@/plugins/helper.js'
import { ModelSelect } from 'vue-search-select';
import "vue-search-select/dist/VueSearchSelect.css"

export default {

   components: {
      ModelSelect
   },

   data() {
      return {
         showAllocateModal: false,
         selectedApiKey: null,
         selectedIndex: 0,
         selectedOrgUuid: null,
         headers: [
            { text: "Extension Key", value: "extensionApiKey" },
            { text: "IP", value: "ip" },
            { text: "Actions", value: "actions" },
         ],
         search: "",
         unallocatedExtensionKeys: [],
         debounceFn: null,
         paginationData: {},
         organisations: this.$store.state.auth.organisations,
         options: [],
         valuePair: [],
         pagination: {
            pageSize: 50, page: 1, totalItems: 0, totalPages: 1,
         },
         rowsPerPageList: [
            { text: "50", value: 50 },
            { text: "75", value: 75 },
            { text: "100", value: 100 },
         ],
         sortByList: [
            { text: "Date: Newest", value: "dateCreated_desc" },
            { text: "Date: Oldest", value: "dateCreated_asc" },
         ],
         sortBy: "dateCreated_desc",
         sorting: { field: "name", order: "desc" },
      }
   },

   computed: {
      paginationLabel() {
         var start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems)
         var end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems)
         return start + " - " + end
      },
      currentPage() { return this.pagination.page },
      rowsPerPage() { return this.pagination.pageSize },
      sortItems() { return this.sortBy },
   },

   watch: {
      currentPage() { this.getUnallocatedExtensionKeys() },
      rowsPerPage() { this.pagination.page = 1; this.getUnallocatedExtensionKeys() },
      sortItems() {
         if (this.sortBy.includes("_")) {
            let sort = this.sortBy.split("_")
            this.sorting.field = sort[0]
            this.sorting.order = sort[1]
         } else {
            this.sorting.field = this.sortBy
            this.sorting.order = ""
         }
         this.getUnallocatedExtensionKeys()
      },
   },

   created() {
      this.init()
      this.debounceFn = debounce(() => this.getUnallocatedExtensionKeys(), 500)

      if (this.$store.state.auth.organisations.length != null) {
         this.$store.state.auth.organisations.forEach((org) => {
            this.options.push({ text: org.name, value: org.orguuid })
            this.valuePair.push({ text: org.name, value: org.orguuid })
         })
      }
   },

   methods: {
      init() { this.getUnallocatedExtensionKeys() },

      formatDate(date) { return moment(date).format("DD-MMM-YYYY @ HH:mm:ss") },

      previousPage() { this.pagination.page > 1 ? this.pagination.page -= 1 : '' },
      nextPage() { this.pagination.totalPages > this.pagination.page ? this.pagination.page += 1 : '' },

      onSearch() {
         this.debounceFn()
         this.pagination.page = 1
      },

      openAllocateModal(apiKey, index) {
         this.selectedApiKey = apiKey
         this.selectedIndex = index
         this.selectedOrgUuid = null
         this.showAllocateModal = true
      },

      closeAllocateModal() {
         this.showAllocateModal = false
         this.selectedApiKey = null
         this.selectedOrgUuid = null
      },

      getOrgNameFromOptions(orguuid) {
         return this.valuePair.find((pair) => pair.value == orguuid)
      },

      truncateText(text, limit) {
         if (text === null || text === '') return ''
         if (text.length <= limit) return text
         return text.slice(0, limit) + '...'
      },

      async allocateExtensionKeyToOrganisation() {
         try {
            const payload = {
               id: this.selectedApiKey.id,
               org_uuid: this.selectedOrgUuid
            }
            const resp = await this.$axios.post("admin-controls/allocate-extension-key-to-organisation", payload)
            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               let pair = this.getOrgNameFromOptions(this.selectedOrgUuid)
               this.$toast.success("Extension key allocated to " + (pair ? pair.text : "organisation"))
                this.getUnallocatedExtensionKeys()
            }
            this.closeAllocateModal()
         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to allocate extension key."))
            this.closeAllocateModal()
         }
      },

      async getUnallocatedExtensionKeys() {
         let apiPath = "admin-controls/get-unllocated-extension-keys?page_size=" + this.pagination.pageSize + "&page=" + this.pagination.page

         if (this.sorting.order != "") {
            apiPath += "&order_by=" + this.sorting.order
         }

         if (this.search !== "" || this.search !== null) {
            apiPath += "&api_key=" + this.search
         }

         await this.$axios.get(apiPath)
            .then((response) => {
               if (response.data.unallocated_extension_api_keys === "No Record Found")
                  this.unallocatedExtensionKeys = []
               else {
                  this.unallocatedExtensionKeys = response.data.unallocated_extension_api_keys
               }
               this.paginationData = response.data.pagination
               this.pagination.totalItems = this.paginationData.total_count
               this.pagination.totalPages = Math.ceil(this.paginationData.total_count / this.pagination.pageSize)
            })
            .catch((error) => {
               this.$toast.error(extractErrorMessage(error, "Failed to load unallocated extension keys."))
            })
      },
   },
}
</script>

<style scoped>
.card { border: none; }

.btn-outline-info { color: #00A0C1; border-color: #00A0C1; }
.btn-outline-info:hover { color: #fff; background-color: #00A0C1; border-color: #00A0C1; }

/* Modal */
.modal-backdrop-custom {
   position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
   background-color: rgba(0,0,0,0.5); display: flex;
   justify-content: center; align-items: center; z-index: 1050;
}
.modal-content-custom {
   position: relative; background: #fff; padding: 2rem;
   border-radius: 14px; width: 100%; max-width: 520px;
   box-shadow: 0 8px 30px rgba(0,0,0,0.2);
}
.modal-title-custom { font-weight: 700; font-size: 1.1rem; color: #435666; margin-bottom: 0; }
.modal-content-custom hr { margin: 0.75rem 0 1rem; border-color: #e9ecef; }
.close-button-custom {
   position: absolute; top: 0.75rem; right: 1rem;
   font-size: 1.8rem; font-weight: bold; line-height: 1;
   color: #adb5bd; background: none; border: none; cursor: pointer;
}
.close-button-custom:hover { color: #435666; }
.modal-body-custom { font-size: 14px; color: #435666; }
.modal-footer-custom { margin-top: 1rem; display: flex; justify-content: flex-end; gap: 8px; }
.btn-modal-cancel { background: #e9ecef; border: none; border-radius: 8px; color: #435666; }

.filter-label {
   font-size: 11px; font-weight: 600; color: #6c757d;
   text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px;
   display: block;
}

.key-display {
   font-family: monospace; font-size: 12px;
   background: #f5f6f8; padding: 8px 12px;
   border-radius: 8px; color: #435666;
   word-break: break-all;
}
</style>